import { useEffect, useRef, useState, useCallback } from "react";
import { useApiHealth } from "@/hooks/api-health";
import { healthStore } from "@/hooks/api-health/healthStore";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth";
import { createScopedLogger } from "@/utils/debug";
import { useRealtimeWebSocket } from "@/contexts/RealtimeWebSocketContext";
import { useSiteContext } from "@/contexts/site";

export function useHealthIndicator({ showToast = true }) {
  const { user } = useAuth();
  const { activeSite } = useSiteContext();
  const toastShownRef = useRef<boolean>(false);
  const toastDebouncerRef = useRef<number | null>(null);
  const activeCustomerRef = useRef<boolean>(!!localStorage.getItem('active_customer'));
  const [connectionStable, setConnectionStable] = useState(false);
  const [lastHealthCheckTime, setLastHealthCheckTime] = useState<number>(0);
  const [hasPartialIssues, setHasPartialIssues] = useState<boolean>(false);
  const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);
  const errorToastsRef = useRef<Set<string>>(new Set());
  const connectionStateRef = useRef<boolean | null>(null);
  const connectionStateTimeoutRef = useRef<number | null>(null);
  const unstableDetectedRef = useRef<boolean>(false);
  const websocketRequestTimestampRef = useRef<number | null>(null);

  // Create a logger function that returns the scoped logger object
  const logger = useCallback(() => createScopedLogger(user, 'HealthIndicator'), [user]);
  
  // Helper function to log debug messages using the enhanced logger
  const logDebug = useCallback((message: string, data?: any) => {
    logger().debug(message, data);
  }, [logger]);

  const {
    isHealthy,
    isChecking,
    healthData,
    setHealthData,
    checkHealth
  } = useApiHealth({
    periodicCheck: false // We'll handle periodic checks ourselves
  });

  // Use a ref to always have the latest callback
  const onMessageRef = useRef<(msg: any) => void>();
  onMessageRef.current = useCallback((msg) => {
    // Parse the message if it's a string
    let parsedMsg;
    try {
      parsedMsg = typeof msg === 'string' ? JSON.parse(msg) : msg;
    } catch (error) {
      return;
    }

    // Handle WebSocket messages
    if (parsedMsg.type === 'health.status') {
      const data = parsedMsg.data;
      if (data) {
        // Get response time from websocket_latency in the data
        const responseTime = data.websocket_latency || null;
        
        if (responseTime !== null) {
          setLastResponseTime(responseTime);
          healthStore.setLastResponseTime(responseTime);
        }
        
        const healthStatus = 
          typeof data.status === 'boolean' ? data.status :
          (data.status === 'healthy' || data.status === 'ok') ? true : false;
        
        healthStore.setHealthStatus(healthStatus);
        
        // Add the response time to the healthData if we have it
        const enhancedHealthData = {
          ...data,
          status: healthStatus,
          services: data.services || {},
          errors: data.errors || null,
          customer_services: data.customer_services,
          response_times: data.response_times,
          customer_response_times: data.customer_response_times,
          websocket_latency: responseTime // Ensure websocket_latency is set
        };
        
        setHealthData(enhancedHealthData);
        
        // Explicitly clear the checking status when we receive a response
        healthStore.setIsChecking(false);
        
        setLastHealthCheckTime(Date.now());
        
        const anyServiceDown = checkForPartialIssues(data);
        setHasPartialIssues(anyServiceDown);
        
        // If previously detected an unstable connection and now it's healthy
        if (unstableDetectedRef.current && healthStatus === true) {
          unstableDetectedRef.current = false;
          
          if (showToast && activeCustomerRef.current) {
            debouncedToast('Connection restored.', false);
            toastShownRef.current = false;
          }
        }
        
        if (showToast && activeCustomerRef.current) {
          if (data.status === false && !data.errors && !errorToastsRef.current.has('general_health_issue')) {
            debouncedToast('System health issue detected', true);
            
            errorToastsRef.current.add('general_health_issue');
            unstableDetectedRef.current = true;
            
            setTimeout(() => {
              errorToastsRef.current.delete('general_health_issue');
            }, 60000);
          }
          
          if (data.errors) {
            // Handle specific error types
            Object.entries(data.errors).forEach(([errorType, errorMessage]) => {
              const errorKey = `error_${errorType}`;
              if (!errorToastsRef.current.has(errorKey)) {
                debouncedToast(`Health error: ${errorMessage}`, true);
                errorToastsRef.current.add(errorKey);
                
                setTimeout(() => {
                  errorToastsRef.current.delete(errorKey);
                }, 60000);
              }
            });
          }
        }
      }
    }
  }, [showToast]);

  // Use the realtime WebSocket for connection state and subscribing
  const { connected, subscribe, send } = useRealtimeWebSocket();

  // Subscribe to health status messages
  useEffect(() => {
    const unsubscribe = subscribe((msg) => {
      if (onMessageRef.current) onMessageRef.current(msg);
    });
    return unsubscribe;
  }, [subscribe]);

  // Monitor connection stability with a debounced state change
  useEffect(() => {
    // Clear existing timeout if connection state changes
    if (connectionStateTimeoutRef.current !== null) {
      window.clearTimeout(connectionStateTimeoutRef.current);
      connectionStateTimeoutRef.current = null;
    }
    
    // If connection state has changed
    if (connectionStateRef.current !== connected) {
      connectionStateRef.current = connected;
      
      if (connected) {
        // When connection becomes available, wait a bit before setting as stable
        connectionStateTimeoutRef.current = window.setTimeout(() => {
          setConnectionStable(true);
          connectionStateTimeoutRef.current = null;
          
          // Force a health check when connection is newly established
          checkStatusNow(); // Use checkStatusNow instead of checkHealth
          unstableDetectedRef.current = false;
        }, 2000); // Wait 2 seconds to confirm connection is stable
      } else {
        // When connection is lost, immediately set as unstable
        setConnectionStable(false);
        unstableDetectedRef.current = true;
      }
    }
    
    return () => {
      if (connectionStateTimeoutRef.current !== null) {
        window.clearTimeout(connectionStateTimeoutRef.current);
      }
    };
  }, [connected]);

  const checkForPartialIssues = (healthData: any): boolean => {
    if (healthData.services) {
      const serviceValues = Object.values(healthData.services) as boolean[];
      const someDown = serviceValues.some(status => status === false);
      const someUp = serviceValues.some(status => status === true);
      
      if (someDown && someUp) {
        return true;
      }
    }
    
    if (healthData.customer_services) {
      for (const site in healthData.customer_services) {
        const siteServices = healthData.customer_services[site];
        const siteServiceValues = Object.values(siteServices) as boolean[];
        const someDown = siteServiceValues.some(status => status === false);
        const someUp = siteServiceValues.some(status => status === true);
        
        if (someDown && someUp) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Debounced toast to prevent flashing messages for brief connection issues
  const debouncedToast = (message: string, isError: boolean) => {
    if (toastDebouncerRef.current) {
      window.clearTimeout(toastDebouncerRef.current);
      toastDebouncerRef.current = null;
    }
    
    toastDebouncerRef.current = window.setTimeout(() => {
      if (isError) {
        toast({
          variant: "destructive",
          title: "Error",
          description: message,
        });
      } else {
        toast({
          description: message,
        });
      }
      toastDebouncerRef.current = null;
    }, 1000);
  };

  // Handle customer selection events
  useEffect(() => {
    const handleCustomerSelected = () => {
      activeCustomerRef.current = true;
      
      // Reset connection stability when customer changes
      setConnectionStable(false);
      unstableDetectedRef.current = false;
      
      // Clear any existing timeouts
      if (connectionStateTimeoutRef.current !== null) {
        window.clearTimeout(connectionStateTimeoutRef.current);
        connectionStateTimeoutRef.current = null;
      }
      
      // Force a health check after customer selection
      setTimeout(() => {
        checkStatusNow();
      }, 1000);
    };
    
    window.addEventListener('customer-selected', handleCustomerSelected);
    
    return () => {
      window.removeEventListener('customer-selected', handleCustomerSelected);
    };
  }, []);

  // Periodic health checks when connected and stable
  useEffect(() => {
    if (!connected || !connectionStable) {
      return;
    }
    
    const intervalId = setInterval(() => {
      checkStatusNow();
    }, 30000); // Check every 30 seconds
    
    return () => {
      clearInterval(intervalId);
    };
  }, [connected, connectionStable]);

  // Function to send health check via WebSocket
  const checkStatusNow = () => {
    const healthCheckMessage = {
      action: 'health.check',
      data: { site_id: activeSite?.id },
      ts: Date.now(),
    };
    send(JSON.stringify(healthCheckMessage));
  };

  return {
    isHealthy,
    isChecking,
    healthData,
    connected,
    connectionStable,
    lastHealthCheckTime,
    hasPartialIssues,
    lastResponseTime,
    checkStatusNow,
  };
}

const formatServiceName = (name: string): string => {
  return name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};
